import { ForwardedRef, Ref, RefCallback, useCallback } from 'react';

type PossibleRef<T> = Ref<T> | undefined;

/**
 * Composes two or more refs together providing a single interface to set their value
 * @param refs
 * @returns a new ref, which translates all changes to {refs}
 *
 * @see {@link composeRefs} a version without buit-in memoization
 * @example
 * const Component = forwardRef((props, forwardedRef) => {
 *   const ownRef = useRef();
 *   const domRef = useComposedRefs(forwardedRef, ownRef); // 👈 merge together
 *   return <div ref={domRef}>...</div>
 * }
 */
export function useComposedRefs<T = unknown>(...refs: PossibleRef<T>[]): RefCallback<T> {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useCallback(composeRefs(...refs), refs);
}

export function composeRefs<T = unknown>(...refs: PossibleRef<T>[]) {
  return (node: T | null) => {
    refs.forEach((ref) => assignRef(ref, node));
  };
}

type AssignableRef<T> = ForwardedRef<T> | undefined;

export function assignRef<T = unknown>(ref: AssignableRef<T>, value: T | null) {
  if (typeof ref === 'function') {
    ref(value);
  } else if (typeof ref === 'object' && ref !== null && 'current' in ref) {
    ref.current = value;
  }
}
